import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import styles from './RichTextEditor.module.css';
import { isEquivalentRichTextHtml, normalizeRichTextHtml } from '../utils/sanitizeRichTextHtml';

type MentionOption = {
  id: string;
  name: string;
  subtitle?: string;
};

type MentionTrigger = {
  start: number;
  end: number;
  query: string;
};

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  ariaLabel?: string;
  mentionSuggestionsProvider?: (query: string) => Promise<MentionOption[]>;
  mentionSuggestionsLabel?: string;
};

const findMentionTrigger = (text: string): { query: string; mentionLength: number } | null => {
  const atIndex = text.lastIndexOf('@');
  if (atIndex < 0) {
    return null;
  }

  if (atIndex > 0 && !/\s/.test(text[atIndex - 1])) {
    return null;
  }

  const rawQuery = text.slice(atIndex + 1);
  if (/\s/.test(rawQuery)) {
    return null;
  }

  return {
    query: rawQuery,
    mentionLength: rawQuery.length + 1,
  };
};

export const RichTextEditor: React.FC<Props> = ({
  value,
  onChange,
  placeholder,
  minHeight = 80,
  ariaLabel,
  mentionSuggestionsProvider,
  mentionSuggestionsLabel,
}) => {
  const [mentionTrigger, setMentionTrigger] = useState<MentionTrigger | null>(null);
  const [mentionSuggestions, setMentionSuggestions] = useState<MentionOption[]>([]);
  const [mentionMenuOpen, setMentionMenuOpen] = useState(false);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const mentionRequestIdRef = useRef(0);

  const mentionsEnabled = useMemo(() => Boolean(mentionSuggestionsProvider), [mentionSuggestionsProvider]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
    ],
    content: value,
    onUpdate({ editor: e }) {
      const html = e.isEmpty ? '' : e.getHTML();
      onChange(html);

      if (!mentionsEnabled) {
        return;
      }

      const selection = e.state.selection;
      const textBeforeCaret = e.state.doc.textBetween(0, selection.from, '\n', '\0');
      const trigger = findMentionTrigger(textBeforeCaret);
      if (!trigger) {
        setMentionTrigger(null);
        setMentionSuggestions([]);
        setMentionMenuOpen(false);
        setMentionActiveIndex(0);
        return;
      }

      const start = selection.from - trigger.mentionLength;
      if (start < 0) {
        setMentionTrigger(null);
        setMentionSuggestions([]);
        setMentionMenuOpen(false);
        setMentionActiveIndex(0);
        return;
      }

      setMentionTrigger({
        start,
        end: selection.from,
        query: trigger.query,
      });
    },
  });

  const closeMentionMenu = useCallback(() => {
    setMentionMenuOpen(false);
    setMentionActiveIndex(0);
    setMentionSuggestions([]);
    setMentionTrigger(null);
  }, []);

  const onSelectMention = useCallback((option: MentionOption) => {
    if (!editor || !mentionTrigger) {
      return;
    }

    editor
      .chain()
      .focus()
      .deleteRange({ from: mentionTrigger.start, to: mentionTrigger.end })
      .insertContent(`@${option.name} `)
      .run();

    closeMentionMenu();
  }, [closeMentionMenu, editor, mentionTrigger]);

  const onEditorKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!mentionMenuOpen || mentionSuggestions.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setMentionActiveIndex(current => (current + 1) % mentionSuggestions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMentionActiveIndex(current => (current - 1 + mentionSuggestions.length) % mentionSuggestions.length);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      onSelectMention(mentionSuggestions[mentionActiveIndex]);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeMentionMenu();
    }
  }, [closeMentionMenu, mentionActiveIndex, mentionMenuOpen, mentionSuggestions, onSelectMention]);

  useEffect(() => {
    if (!mentionsEnabled || !mentionSuggestionsProvider) {
      return;
    }

    if (!mentionTrigger) {
      mentionRequestIdRef.current += 1;
      setMentionSuggestions([]);
      setMentionMenuOpen(false);
      setMentionActiveIndex(0);
      return;
    }

    const requestId = mentionRequestIdRef.current + 1;
    mentionRequestIdRef.current = requestId;

    const timeoutId = window.setTimeout(async () => {
      try {
        const options = await mentionSuggestionsProvider(mentionTrigger.query);
        if (requestId !== mentionRequestIdRef.current) {
          return;
        }

        setMentionSuggestions(options);
        setMentionMenuOpen(options.length > 0);
        setMentionActiveIndex(0);
      } catch {
        if (requestId !== mentionRequestIdRef.current) {
          return;
        }

        setMentionSuggestions([]);
        setMentionMenuOpen(false);
      }
    }, 120);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [mentionSuggestionsProvider, mentionTrigger, mentionsEnabled]);

  // Sync external value changes (e.g. form reset / task load), but avoid clobbering active edits.
  useEffect(() => {
    if (!editor) return;
    if (editor.isFocused) return;

    const current = editor.isEmpty ? '' : editor.getHTML();
    if (isEquivalentRichTextHtml(current, value)) {
      return;
    }

    editor.commands.setContent(normalizeRichTextHtml(value), { emitUpdate: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const btn = (label: string, active: boolean, onClick: () => void, title: string) => (
    <button
      key={title}
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={`${styles.toolbarBtn}${active ? ` ${styles.active}` : ''}`}
      onMouseDown={e => { e.preventDefault(); onClick(); }}
    >
      {label}
    </button>
  );

  return (
    <div className={styles.wrapper} aria-label={ariaLabel} role="group">
      <div className={styles.toolbar} role="toolbar" aria-label="Text formatting">
        {btn('B', editor.isActive('bold'),        () => editor.chain().focus().toggleBold().run(),          'Bold')}
        {btn('I', editor.isActive('italic'),      () => editor.chain().focus().toggleItalic().run(),        'Italic')}
        {btn('U', editor.isActive('underline'),   () => editor.chain().focus().toggleUnderline().run(),     'Underline')}
        {btn('S̶', editor.isActive('strike'),      () => editor.chain().focus().toggleStrike().run(),        'Strikethrough')}
        <span className={styles.toolbarDivider} />
        {btn('H1', editor.isActive('heading', { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), 'Heading 1')}
        {btn('H2', editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), 'Heading 2')}
        <span className={styles.toolbarDivider} />
        {btn('≡', editor.isActive('bulletList'),  () => editor.chain().focus().toggleBulletList().run(),    'Bullet list')}
        {btn('①', editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(),   'Numbered list')}
        <span className={styles.toolbarDivider} />
        {btn('"', editor.isActive('blockquote'),  () => editor.chain().focus().toggleBlockquote().run(),    'Blockquote')}
        {btn('<>', editor.isActive('code'),        () => editor.chain().focus().toggleCode().run(),          'Inline code')}
        <span className={styles.toolbarDivider} />
        {btn('✕', false, () => editor.chain().focus().clearNodes().unsetAllMarks().run(), 'Clear formatting')}
      </div>
      <div className={styles.editorArea} style={{ minHeight }} onClick={() => editor.commands.focus()}>
        <EditorContent editor={editor} onKeyDown={onEditorKeyDown} />

        {mentionMenuOpen && mentionSuggestions.length > 0 ? (
          <div className={styles.mentionMenu} role="listbox" aria-label={mentionSuggestionsLabel || 'Mention suggestions'}>
            {mentionSuggestions.map((option, index) => {
              const isActive = index === mentionActiveIndex;
              return (
                <button
                  key={option.id}
                  type="button"
                  className={`${styles.mentionMenuItem} ${isActive ? styles.mentionMenuItemActive : ''}`}
                  onMouseDown={event => {
                    event.preventDefault();
                    onSelectMention(option);
                  }}
                >
                  <span className={styles.mentionMenuName}>@{option.name}</span>
                  {option.subtitle ? <span className={styles.mentionMenuMeta}>{option.subtitle}</span> : null}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};
